/**
 * SymbolList — per-segment symbol multi-select component.
 *
 * Renders a `react-multi-select-component` dropdown for one market segment.
 * Symbols are sorted alphabetically.  Selecting and clicking "Add" dispatches
 * `addScripsReducer` + `addScripIdsReducer` to append them to the watchlist.
 *
 * Used exclusively inside `MarketSegmentList` (one per feed/segment).
 */
import { useEffect, useMemo, useState } from "react";
import { MultiSelect } from "react-multi-select-component";
import { useDispatch, useSelector } from "react-redux";
import { HiOutlinePlus } from "react-icons/hi";
import { addScripsReducer, addScripIdsReducer } from "@store/watchlist-slice";
import { syncMarketWatchScrips } from "@service/market-watch-handler";

interface Option {
  label: string;
  value: string;
}

const SymbolList = ({ feed, selectedSymbolsData, setSelectedSymbolsData }) => {
  const dispatch = useDispatch();

  const { scrips } = useSelector(({ watchlist }) => watchlist);

  const [selectedSymbolValue, setSelectedSymbolValue] = useState<Option[]>([]);

  const options = useMemo(() => {
    return feed.scrips
      .map((symbol) => ({
        label: symbol.symbol,
        value: symbol.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [feed.scrips]);

  useEffect(() => {
    setSelectedSymbolValue(selectedSymbolsData[parseInt(feed.id) - 1] || []);
  }, []);

  const selectValueRenderer = () => {
    const selectedSymbolsCount = selectedSymbolsData[parseInt(feed.id) - 1]
      ? selectedSymbolsData[parseInt(feed.id) - 1].length
      : 0;

    if (selectedSymbolsCount === 0) {
      return `Select symbols`;
    }

    if (selectedSymbolsCount === options.length) {
      return "Selected All";
    }

    return selectedSymbolsCount > 1
      ? `${selectedSymbolsCount} symbols selected`
      : `${selectedSymbolsCount} symbol selected`;
  };

  const handleSelectChange = (selected: Option[]) => {
    setSelectedSymbolsData((prevSelectedSymbolsData) => {
      const updatedSelectedSymbolsData = [...prevSelectedSymbolsData];
      updatedSelectedSymbolsData[parseInt(feed.id) - 1] = selected;
      return updatedSelectedSymbolsData;
    });

    setSelectedSymbolValue(selected);
  };

  const handleAddButtonClick = async () => {
    if (!!!selectedSymbolValue) {
      return;
    }
    const symbolIdList = scrips.map((symbol) => parseInt(symbol.id));

    const newScrips = selectedSymbolValue
      .filter((symbol) => !symbolIdList.includes(parseInt(symbol.value)))
      .map((symbol) => {
        return {
          id: symbol.value,
          symbol: symbol.label,
          segment: feed.name,
          ltp: null,
          isHigh: false,
        };
      });

    const uniqueScrips = [...scrips, ...newScrips];

    dispatch(addScripsReducer(uniqueScrips));
    dispatch(addScripIdsReducer(uniqueScrips.map((scrip) => scrip.id)));
    // Bulk add from the Add Symbols dialog — same panel, so same persisted list.
    syncMarketWatchScrips(uniqueScrips.map((scrip) => scrip.id));
  };

  return (
    <div className="symbol-list">
      <div className="symbol-container">
        <div className="symbol-flex">
          <div className="futures-dropdown">
            <div className="buttonlabel-container">
              <div>
                <label htmlFor="feed-label" className="symbol-label">
                  {feed.name}
                </label>
              </div>
              {selectedSymbolsData[parseInt(feed.id) - 1]?.length ? (
                <div>
                  <button
                    type="submit"
                    className="addwatchlist-btn"
                    onClick={handleAddButtonClick}
                  >
                    <HiOutlinePlus />
                    <span>Add</span>
                  </button>
                </div>
              ) : (
                ""
              )}
            </div>
            <div className="multiselect">
              <MultiSelect
                options={options}
                hasSelectAll
                labelledBy={`Select ${feed.name}`}
                className="multiselect"
                value={selectedSymbolValue}
                valueRenderer={selectValueRenderer}
                onChange={handleSelectChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SymbolList;
